

















import {
  defineComponent,
  ref,
  watch,
  onMounted,
  useRoute,
  PropType,
  useRouter,
} from "@nuxtjs/composition-api";
import type { AggregationOption } from "~/modules/GraphQL/types";
import { debounce } from "lodash";
import type { RemovableFilterInterface } from "~/modules/catalog/category/components/filters/useFilters";

export default defineComponent({
  name: "PriceRangeSlider",
  props: {
    propMin: {
      type: [Number],
      default: 0,
    },
    propMax: {
      type: [Number],
      default: 2000000,
    },
    propStep: {
      type: [Number, String],
      default: 1000,
    },
    filterPrice: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    removableFilters: {
      type: Array as PropType<RemovableFilterInterface[]>,
      required: false,
      default: () => [],
    },
    selectedPrice: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const min = ref<number>(Number(props.propMin) || 0);
    const max = ref<number>(Number(props.propMax) || 2000000);
    const minValue = ref<number>(Number(props.propMin) || 0);
    const maxValue = ref<number>(Number(props.propMax) || 2000000);
    const step = ref<number>(Number(props.propStep) || 1000);
    const totalSteps = ref<number>(0);
    const percentPerStep = ref<number>(1);
    const isDragging = ref<boolean>(false);
    const pos = ref<{ curTrack: string | null }>({ curTrack: null });

    const track = ref<HTMLElement | null>(null);
    const trackHighlight = ref<HTMLElement | null>(null);
    const track1 = ref<HTMLElement | null>(null);
    const track2 = ref<HTMLElement | null>(null);
    // Use this flag to track if values changed during dragging
    const valuesChanged = ref(false);

    const valueToPercent = (value) => {
      return ((value - min.value) / step.value) * percentPerStep.value;
    };

    const setTrackHighlight = () => {
      trackHighlight.value.style.left = valueToPercent(minValue.value) + "%";
      trackHighlight.value.style.width =
        valueToPercent(maxValue.value) - valueToPercent(minValue.value) + "%";
    };

    const getPercentInPx = () => {
      const trackWidth = track.value.offsetWidth;
      const oneStepInPx = trackWidth / totalSteps.value;
      return oneStepInPx / percentPerStep.value;
    };

    // Ensure max and propMax are always in sync
    const syncMaxValues = () => {
      max.value = Number(props.propMax) || 2000000;
    };

    // Same for min values
    const syncMinValues = () => {
      min.value = Number(props.propMin) || 0;
    };

    const moveTrack = (trackName, ev) => {
      const percentInPx = getPercentInPx();
      const trackX = Math.round(track.value.getBoundingClientRect().left);
      const trackWidth = track.value.offsetWidth;
      const clientX =
        ev.clientX || (ev.touches && ev.touches[0] ? ev.touches[0].clientX : 0);
      const moveDiff = clientX - trackX;
      const moveInPct = moveDiff / percentInPx;

      const snapThreshold = 1.5;

      if (moveInPct < 0 || moveInPct > 100) return;
      let value =
        Math.round(moveInPct / percentPerStep.value) * step.value + min.value;

      if (isNaN(value)) return;

      if (trackName === "track1") {
        // Store old value for change detection
        const oldValue = minValue.value;

        // Snap to min if close enough
        if (moveInPct <= snapThreshold) {
          syncMinValues();
          value = min.value;
        }

        // Ensure value doesn't exceed max
        if (value >= maxValue.value) return;

        // Set the new value
        if (oldValue !== value) {
          minValue.value = value;
          valuesChanged.value = true;
        }
      }

      if (trackName === "track2") {
        // Store old value for change detection
        const oldValue = maxValue.value;

        // Snap to max if close enough
        if (moveInPct >= 100 - snapThreshold) {
          syncMaxValues();
          value = max.value;
        }

        // Ensure value is greater than min
        if (value <= minValue.value + step.value) return;

        // Set the new value
        if (oldValue !== value) {
          maxValue.value = value;
          valuesChanged.value = true;
        }
      }

      // Update UI only - no emit during dragging
      track1.value.style.left = valueToPercent(minValue.value) + "%";
      track2.value.style.left = valueToPercent(maxValue.value) + "%";
      setTrackHighlight();
    };

    // Add a helper to emit price change with delay
    const emitPriceChange = (minVal, maxVal) => {
      // Ensure we use exact prop values for extremes
      if (minVal <= Number(props.propMin) + step.value / 2) {
        syncMinValues();
        minVal = min.value;
      }

      if (maxVal >= Number(props.propMax) - step.value / 2) {
        syncMaxValues();
        maxVal = max.value;
      }

      // Create the option object for filter
      const option: AggregationOption = {
        count: 0,
        label: `${new Intl.NumberFormat("en-DE").format(
          minVal
        )}-${new Intl.NumberFormat("en-DE").format(maxVal)}`,
        value: `${minVal}_${maxVal}`,
      };

      // Reset the change flag
      valuesChanged.value = false;

      // Emit event
      emit("changePrice", option);
    };

    const setClickMove = (ev) => {
      const trackRect = track.value.getBoundingClientRect();
      const track1Left = track1.value.getBoundingClientRect().left;
      const track2Left = track2.value.getBoundingClientRect().left;
      // Xử lý cho cả touch và mouse events
      const clientX =
        ev.clientX || (ev.touches && ev.touches[0] ? ev.touches[0].clientX : 0);

      // Calculate position as percentage of track width
      const posPercent = ((clientX - trackRect.left) / trackRect.width) * 100;

      // Record old values to detect changes
      const oldMinValue = minValue.value;
      const oldMaxValue = maxValue.value;

      // Snap to min/max if click is near edges
      if (posPercent <= 5) {
        syncMinValues();
        minValue.value = min.value;
        track1.value.style.left = valueToPercent(minValue.value) + "%";
        setTrackHighlight();
        valuesChanged.value = oldMinValue !== minValue.value;
      } else if (posPercent >= 95) {
        syncMaxValues();
        maxValue.value = max.value;
        track2.value.style.left = valueToPercent(maxValue.value) + "%";
        setTrackHighlight();
        valuesChanged.value = oldMaxValue !== maxValue.value;
      } else {
        // Otherwise use normal logic
        if (clientX < track1Left) {
          moveTrack("track1", ev);
        } else if (clientX - track1Left < track2Left - clientX) {
          moveTrack("track1", ev);
        } else {
          moveTrack("track2", ev);
        }
      }

      // Emit after click with delay
      if (valuesChanged.value) {
        emitPriceChange(minValue.value, maxValue.value);
      }
    };

    // Replace debounced functions with simple setters
    const changeMin = (newValue) => {
      minValue.value = newValue;
      valuesChanged.value = true;
    };

    const changeMax = (newValue) => {
      maxValue.value = newValue;
      valuesChanged.value = true;
    };

    watch(
      () => props.propMin,
      (newVal) => {
        syncMinValues();
        minValue.value = Number(newVal) || 0;
        recalculateSlider(); // Add this to update the UI when min value changes
      }
    );

    watch(
      () => props.propMax,
      (newVal) => {
        syncMaxValues();
        maxValue.value = Number(newVal) || 2000000;
        recalculateSlider(); // Add this to update the UI when max value changes
      }
    );

    watch(minValue, (newValue, oldValue) => {
      if (newValue !== props.propMin) {
        changeMin(newValue);
      } else {
        minValue.value = props.propMin;
        min.value = props.propMin;
        if (track1.value) {
          recalculateSlider(); // Replace the individual style settings with our method
        }
      }
    });

    watch(maxValue, (newValue, oldValue) => {
      if (newValue !== props.propMax) {
        changeMax(newValue);
      } else {
        maxValue.value = props.propMax;
        max.value = props.propMax;
        if (track2.value) {
          recalculateSlider(); // Replace the individual style settings with our method
        }
      }
    });

    watch(
      () => props.filterPrice,
      () => {
        setTimeout(() => {
          if (props.filterPrice) {
            minValue.value = props.filterPrice.from;
            maxValue.value = props.filterPrice.to;
          }
        }, 1000);
      }
    );

    watch(
      () => route.value?.query?.price,
      () => {
        loadData();
      }
    );

    watch(
      () => props.removableFilters,
      () => {
        if (
          props.removableFilters.filter((e) => e.type == "swatch_price")
            .length == 0 ||
          !props.removableFilters
        ) {
          minValue.value = props.propMin ? props.propMin : 0;
          maxValue.value = props.propMax ? props.propMax : 2000000;
        }
      }
    );

    watch(
      () => props.selectedPrice,
      () => {
        if (!props.selectedPrice) {
          minValue.value = props.propMin ? props.propMin : 0;
          maxValue.value = props.propMax ? props.propMax : 2000000;
        }
      }
    );

    const loadData = () => {
       let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }

      const priceQuery = routerQuery?.price;
      if (typeof priceQuery === "string" && priceQuery) {
        minValue.value = Number(priceQuery.split("_")[0]);
        maxValue.value = Number(priceQuery.split("_")[1]);
      }
    };

    const applyExtremeValues = () => {
      if (minValue.value === min.value) {
        changeMin(minValue.value);
      }
      if (maxValue.value === max.value) {
        changeMax(maxValue.value);
      }
    };

    // Add a new method to recalculate and reposition everything
    const recalculateSlider = () => {
      if (
        !track.value ||
        !track1.value ||
        !track2.value ||
        !trackHighlight.value
      )
        return;

      // Recalculate proportions
      totalSteps.value = (max.value - min.value) / step.value;
      percentPerStep.value = 100 / totalSteps.value;

      // Update positions of sliders
      track1.value.style.left = valueToPercent(minValue.value) + "%";
      track2.value.style.left = valueToPercent(maxValue.value) + "%";

      // Update highlight track
      setTrackHighlight();
    };

    onMounted(() => {
      loadData();
      if (!track1.value || !track2.value) return;

      totalSteps.value = (max.value - min.value) / step.value;
      percentPerStep.value = 100 / totalSteps.value;

      // Use the recalculate method to set initial positions
      recalculateSlider();

      // Apply extreme values if they're already set
      applyExtremeValues();

      const handleMouseMove = (ev) => {
        if (isDragging.value && pos.value.curTrack) {
          moveTrack(pos.value.curTrack, ev);
        }
      };

      // Improved mouseup/touchend handler
      const handleMouseUp = (ev) => {
        if (isDragging.value && pos.value.curTrack) {
          // Set dragging to false first
          isDragging.value = false;
          pos.value.curTrack = null;

          // When releasing at min/max positions, ensure values are exact
          if (minValue.value <= min.value + step.value / 2) {
            syncMinValues();
            minValue.value = min.value;
            valuesChanged.value = true;
          }

          if (maxValue.value >= max.value - step.value / 2) {
            syncMaxValues();
            maxValue.value = max.value;
            valuesChanged.value = true;
          }

          // Update UI to reflect final values
          if (track1.value && track2.value) {
            track1.value.style.left = valueToPercent(minValue.value) + "%";
            track2.value.style.left = valueToPercent(maxValue.value) + "%";
            setTrackHighlight();
          }

          // Emit the price change only after drag is complete
          if (valuesChanged.value) {
            setTimeout(() => {
              emitPriceChange(minValue.value, maxValue.value);
            }, 100); // Small delay for UI to update first
          }
        }
      };

      // Add listeners with explicit capture option for better mobile support
      document.addEventListener("mousemove", handleMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleMouseMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleMouseUp);
      document.addEventListener("touchcancel", handleMouseUp);

      ["mousedown", "touchstart"].forEach((type) => {
        track1.value.addEventListener(
          type,
          (ev) => {
            ev.stopPropagation();
            ev.preventDefault(); // Prevent default to avoid page scrolling on mobile
            isDragging.value = true;
            pos.value.curTrack = "track1";
            valuesChanged.value = false; // Reset change tracking at start of new drag
          },
          { passive: false }
        );

        track2.value.addEventListener(
          type,
          (ev) => {
            ev.stopPropagation();
            ev.preventDefault(); // Prevent default to avoid page scrolling on mobile
            isDragging.value = true;
            pos.value.curTrack = "track2";
            valuesChanged.value = false; // Reset change tracking at start of new drag
          },
          { passive: false }
        );
      });

      // Separate listeners for touchend to ensure they bubble up
      ["mouseup", "touchend"].forEach((type) => {
        track1.value.addEventListener(type, (ev) => {
          if (isDragging.value) {
            handleMouseUp(ev);
          }
        });

        track2.value.addEventListener(type, (ev) => {
          if (isDragging.value) {
            handleMouseUp(ev);
          }
        });
      });

      track.value.addEventListener("click", (ev) => {
        ev.stopPropagation();
        setClickMove(ev);
      });

      trackHighlight.value.addEventListener("click", (ev) => {
        ev.stopPropagation();
        setClickMove(ev);
      });

      // Cleanup event listeners on component unmount
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleMouseMove);
        document.removeEventListener("touchend", handleMouseUp);
        document.removeEventListener("touchcancel", handleMouseUp);
      };
    });

    return {
      minValue,
      maxValue,
      track,
      trackHighlight,
      track1,
      track2,
      moveTrack,
      setClickMove,
      valueToPercent,
      setTrackHighlight,
      isDragging,
      pos,
      recalculateSlider, // Expose the method if needed for external calls
    };
  },
});
